import React, {Fragment, useState} from 'react';
import {Typography} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {useNavigate} from 'react-router-dom';

import {TelehealthBookingEnum} from 'presentation/constants';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {BookingTypeEnum, FlowEnum, TELEMEDICINE_INFO, TelemedicineEnum} from '../../constants';

import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {DetailsDrawer} from '../DetailsDrawer';
import {UnitDetails} from '../DetailsDrawer/types';
import {UnitCard} from '../UnitCard';
import {HandleSelectCardParams, UnitGridProps} from './types';
import {UnitKeys, Units} from 'presentation/modules/booking/shared/store/useUnitsListStore/types';

const {resultFound} = translations['pt-br'].commons;

export const UnitGrid = ({
	list,
	handleNext,
	flowType,
	isFiltered,
	isNotSelfNetwork = false,
}: UnitGridProps) => {
	const {isMobile} = useMediaQueries();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);

	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const navigate = useNavigate();
	const {SEARCH_PARAMS, clearSearch} = useQueryParams();
	const [unitDetailsDrawerState, setUnitDetailsDrawerState] = useState(false);
	const [unitDetailsContentState, setUnitDetailsContentState] = useState<UnitDetails | null>(
		null,
	);

	const {query} = SEARCH_PARAMS;

	const isOdonto = flowType === FlowEnum.ODONTO;
	const hasTelemedicine = list?.some(
		item => (item as Units<FlowEnum.HEALTH>)?.telemedicine === TelemedicineEnum.IS_TELEMEDICINE,
	);
	const showTelemedicineFullGrid = hasTelemedicine && (list?.length === 1 || list?.length >= 3);

	const getIsSelected = (item: Units<UnitKeys>) =>
		isOdonto
			? bookingData?.unitCode === item?.code && bookingData?.districtName === item?.district
			: bookingData?.unitCode === item?.code;

	const handleSelectCard = ({
		code,
		address,
		name,
		district,
		phoneNumbers,
		isTelemedicine,
	}: HandleSelectCardParams) => {
		if (isNotSelfNetwork) {
			setUnitDetailsDrawerState(true);
			setUnitDetailsContentState({
				address,
				name,
				phoneNumbers,
				specialty: bookingData.specialtyName,
			});
			clearSearch();

			return;
		}

		if (isTelemedicine) {
			setBookingData(prev => ({
				...prev,
				unitCode: code,
				unitName: TELEMEDICINE_INFO.name,
				unitAddress: '',
				ufCode: TELEMEDICINE_INFO.ufCode,
				ufName: TELEMEDICINE_INFO.ufName,
				cityName: TELEMEDICINE_INFO.cityName,
				bookingRedirect: true,
				bookingType: BookingTypeEnum.TELEHEALTH,
			}));

			const {PROFESSIONAL_STEP} = getStepPosition({
				flowType: FlowEnum.TELEHEALTH,
				isManualLocation: bookingData.isManualLocation,
			});

			setActiveBookingFlowStep(PROFESSIONAL_STEP);
			navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);

			clearSearch();

			return;
		}

		setBookingData(prev => ({
			...prev,
			unitCode: code,
			unitName: name,
			unitAddress: address,
			bookingRedirect: false,
		}));

		if (flowType === FlowEnum.ODONTO) {
			setBookingData(prev => ({
				...prev,
				districtName: district,
			}));
		}

		clearSearch();
		handleNext();
	};

	return (
		<Fragment>
			<Fragment>
				{query ? (
					<Typography variant="subtitle2" color="text.secondary">
						{resultFound}
					</Typography>
				) : null}
				<Grid2 container spacing={2}>
					{list?.map((item, index) => {
						const isTelemedicine =
							(item as Units<FlowEnum.HEALTH>)?.telemedicine ===
							TelemedicineEnum.IS_TELEMEDICINE;
						const isFullGrid = (isTelemedicine && showTelemedicineFullGrid) || isMobile;

						return (
							<Grid2 key={index} size={{xs: isFullGrid ? 12 : 6}}>
								<UnitCard
									isNotSelf={isNotSelfNetwork}
									query={query}
									isFiltered={isFiltered}
									onClick={() =>
										handleSelectCard({
											district: item?.district,
											code: item?.code,
											address: item?.formattedAddress,
											name: item?.name,
											isTelemedicine,
											phoneNumbers: (item as Units<FlowEnum.HEALTH>)
												?.phoneNumbers,
										})
									}
									isSelected={isNotSelfNetwork ? false : getIsSelected(item)}
									unitName={item?.name}
									address={item?.formattedAddress}
									nmBairroOrigem={
										(item as Units<FlowEnum.HEALTH>)?.nmBairroOrigem
									}
									isTelemedicine={isTelemedicine}
									distanceReference={(item as Units<FlowEnum.HEALTH>)?.distance}
								/>
							</Grid2>
						);
					})}
				</Grid2>
			</Fragment>

			<DetailsDrawer
				open={unitDetailsDrawerState}
				setOpen={setUnitDetailsDrawerState}
				unitData={unitDetailsContentState}
				flowType={flowType}
			/>
		</Fragment>
	);
};
